import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ProviderService } from '../../../../api/Providers/Services';
import {
  useIsMobile,
  useOnce,
  useParams,
} from '../../../../hooks';
import nonCriticalException from '../../../../modules/exceptionLogger';
import { ProviderReviewsResponse, loadReviews } from '../../../../modules/provider/reviews';
import { RootState } from '../../../../store/models';
import { HeaderText, Stack } from '../../../ui';
import { ServiceItem } from '../ServiceItem/ServiceItem';
import { MAX_TOP_SERVICES } from '../constants';
import { buildPopularServices } from '../serviceSelectors';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';

type TopServicesListProps = {
  profile: ClientProviderProfile;
  services: ProviderService[];
  onRatingsPhotoClick: (service: ProviderService) => void;
};

export const TopServicesList: React.FC<TopServicesListProps> = ({
  profile, services, onRatingsPhotoClick,
}) => {
  const routeParams = useParams();
  const isMobile = useIsMobile();
  const [topServiceRatings, setTopServicesRatings] = useState({});

  const highlightedServiceId = routeParams?.matchedServiceId
    ? parseInt(routeParams.matchedServiceId, 10)
    : null;

  const popularServices = useSelector<RootState, ProviderService[]>(
    state => buildPopularServices(state, profile),
  );

  const trimmedPopularServices = useMemo(() => popularServices
    ?.slice(0, MAX_TOP_SERVICES)
    .map((service, index) => ({ ...service, serviceRank: index + 1 })) || [], [popularServices]);

  const trimmedPopularServiceWithoutHighlighted = useMemo(
    () => trimmedPopularServices.filter(service => service.id !== highlightedServiceId) || [],
    [
      trimmedPopularServices,
      highlightedServiceId,
    ],
  );

  const highlightedService = useMemo(
    () => {
      const highlightedServiceFoundOnTopServices = trimmedPopularServices.find(
        service => service.id === highlightedServiceId,
      );
      if (highlightedServiceFoundOnTopServices) {
        return highlightedServiceFoundOnTopServices;
      }
      return services.find(service => service.id === highlightedServiceId);
    },
    [
      highlightedServiceId,
      services,
      trimmedPopularServices,
    ],
  );

  /**
   * This use once is used only to calculate the ratings for the highlighted service
   */
  useOnce(async () => {
    const ratingData: ProviderReviewsResponse = await loadReviews(
      profile.id,
      { exclude_star_only: true, service_id: highlightedService.id },
    );

    let averageStars = 0;
    if (ratingData?.results?.length > 0) {
      const totalStars = ratingData.results.reduce((acc, curr) => acc + curr.num_stars, 0);
      averageStars = totalStars / ratingData.results.length;
    }

    const highlightedServiceRating = {
      ...highlightedService,
      numRatings: ratingData?.count || 0,
      averageStars: averageStars.toFixed(1),
      isHighlightedService: true,
    };

    setTopServicesRatings(
      state => ({ ...state, [highlightedService.id]: highlightedServiceRating }),
    );
  }, highlightedService);

  /**
   * This use once is used to calculate the ratings for the rest of the popular services
   */
  useOnce(async () => {
    const calculatedTopServiceRatings = {};
    try {
      await Promise.all(
        trimmedPopularServiceWithoutHighlighted.map(async service => {
          let averageStars = 0;

          const ratingData: ProviderReviewsResponse = await loadReviews(
            profile.id,
            { exclude_star_only: true, service_id: service.id },
          );

          if (ratingData?.results?.length > 0) {
            const totalStars = ratingData.results.reduce((acc, curr) => acc + curr.num_stars, 0);
            averageStars = totalStars / ratingData.results.length;
          }

          calculatedTopServiceRatings[service.id] = {
            ...service,
            numRatings: ratingData?.count || 0,
            averageStars: averageStars.toFixed(1),
          };

          return topServiceRatings[service.id];
        }),
      );

      setTopServicesRatings(state => ({ ...state, ...calculatedTopServiceRatings }));
    } catch (e) {
      nonCriticalException(
        e,
        {
          error: 'Failed to fetch reviews and photo urls for profile services',
          providerId: profile.id,
          serviceIds: trimmedPopularServices.map(service => service.id),
        },
      );
    }
  }, trimmedPopularServices?.length > 0);

  if (!trimmedPopularServiceWithoutHighlighted?.length && !highlightedService) {
    return null;
  }

  return (
    <Stack
      paddingX={isMobile ? 24 : 0}
      paddingBottom={24}
    >
      <HeaderText level={isMobile ? 3 : 2} testID="top-services-label">Top Services</HeaderText>
      {highlightedService && (
        <ServiceItem
          service={{ ...highlightedService, ...topServiceRatings[highlightedService.id] }}
          providerId={profile.id}
          providerPhone={profile.public_phone_number}
          isOnlineBookable={(
              profile.is_effectively_online_bookable
              && !profile.is_disabled
              && !!highlightedService.is_online_bookable
          )}
          showAndUp={highlightedService.cost_and_up || profile.smart_pricing_enabled}
          onRatingsPhotosClick={onRatingsPhotoClick}
        />
      )}
      {trimmedPopularServiceWithoutHighlighted.map(service => (
        <ServiceItem
          key={service.id}
          service={{ ...service, ...topServiceRatings[service.id] }}
          providerId={profile.id}
          providerPhone={profile.public_phone_number}
          isOnlineBookable={(
              profile.is_effectively_online_bookable
              && !profile.is_disabled
              && !!service.is_online_bookable
          )}
          showAndUp={service.cost_and_up || profile.smart_pricing_enabled}
          onRatingsPhotosClick={onRatingsPhotoClick}
        />
      ))}
    </Stack>
  );
};
